import { invert } from 'lodash';

const SubscriptionTypes = {
  alaCarte: 'alaCarte',
  coreDirectory: 'coreDirectory',
  platform: 'platform',
  platformPlus: 'platformPlus',
  sso: 'sso',
  primePlus: 'primePlus',
  devices: 'devices',
  patchDevices: 'patchDevices',
  policyDevices: 'policyDevices',
};

const SubscriptionFields = {
  addOns: 'addOns',
  annualPrice: 'annualPrice',
  components: 'components',
  displayName: 'displayName',
  features: 'features',
  listPrice: 'listPrice',
  optionalAddOns: 'optionalAddOns',
  productCode: 'productCode',
};

const AddOnProductCodes = {
  deviceMgmt: 'DEVICE-MANAGEMENT-2021',
  passwordManagement: 'PASSWORD-MGMT',
  premiumSupport: 'PREMIUM-SUPPORT-2021',
};

// names from API
const Features = {
  cloudDirectory: 'cloudDirectory',
  cloudDirectoryAndSupport: 'cloudDirectoryAndSupport',
  deviceManagement: 'deviceManagement',
  directoryInsightsPremium: 'directoryInsightsPremium',
  jumpcloudProtect: 'jumpcloudProtect',
  ldap: 'ldap',
  mdm: 'mdm',
  mfa: 'mfa',
  osPatchManagement: 'osPatchManagement',
  passwordManagement: 'passwordManagement',
  premiumSupport: 'premiumSupport',
  radius: 'radius',
  remoteAssist: 'remoteAssist',
  sso: 'sso',
  systemInsights: 'systemInsights',
  userLifecycle: 'userLifecycle',
  zeroTrust: 'zeroTrust',
};

const FeatureFlags = {
  passwordManagerGtm: 'passwordManagerGtm',
};

const Attributes = {
  cloudDirectory: 'cloudDirectory',
  cloudDirectoryAndSupport: 'cloudDirectoryAndSupport',
  cloudLDAP: 'cloudLDAP',
  cloudMFA: 'cloudMFA',
  cloudRADIUS: 'cloudRADIUS',
  conditionalAccessPolicies: 'conditionalAccessPolicies',
  deviceAndServerManagement: 'deviceAndServerManagement',
  deviceManagement: 'deviceManagement', // from API
  deviceTrust: 'deviceTrust',
  directoryInsights: 'directoryInsights',
  jumpcloudProtect: 'jumpcloudProtect',
  mdm: 'mdm',
  networkTrust: 'networkTrust',
  osPatchManagement: 'osPatchManagement',
  passwordManagement: 'passwordManagement',
  premiumSupport: 'premiumSupport', // from API
  remoteAssist: 'remoteAssist',
  standardSupport: 'standardSupport',
  supportAndImplementation: 'supportAndImplementation',
  systemInsights: 'systemInsights',
  userLifecycleManagement: 'userLifecycleManagement',
  SSOToWebApplications: 'SSOToWebApplications',
};

const AttributeDetails = {
  [Attributes.cloudDirectory]: {
    name: 'Cloud Directory & Standard Support',
    icon: 'jcLogo',
  },
  [Attributes.cloudDirectoryAndSupport]: {
    cloudDirectoryName: 'Cloud Directory',
    standardSupportName: 'Standard Support',
    cloudDirectoryDescription: '$3 for initial access to the JumpCloud Admin console. Add the features you need to your plan.',
    standardSupportDescription: 'Besides JumpCloud’s extensive Knowledge Base, you can submit a support ticket or send us an email, and expect a response during normal JumpCloud business hours.',
  },
  [Attributes.cloudMFA]: {
    name: 'Multi-Factor Authentication (MFA)',
    description: 'Multi-factor authentication allows you to easily enforce push notifications, universal second factor (U2F), and TOTP MFA to safeguard your users’ access to applications, devices, networks, and more.',
    icon: 'mfa',
  },
  [Attributes.SSOToWebApplications]: {
    name: 'Single Sign-On (SSO)',
    description: 'Single Sign On allows your users to access all of their work applications with one password from JumpCloud’s User Portal',
    icon: 'sso',
  },
  [Attributes.userLifecycleManagement]: {
    name: 'User Lifecycle Management',
    description: 'Provision, deprovision and manage user access to applications, devices, networks, and servers',
    icon: 'userGroup',
  },
  [Attributes.cloudLDAP]: {
    name: 'Cloud LDAP',
    description: 'Get LDAP (lightweight directory access protocol) without the usual setup, maintenance, or failover requirements of traditional LDAP implementations.',
    icon: 'ldap',
  },
  [Attributes.cloudRADIUS]: {
    name: 'Cloud RADIUS',
    description: 'Enables your organization to authenticate your users to Wi-Fi, VPNs, switches, and network devices securely.',
    icon: 'radius',
  },
  [Attributes.directoryInsights]: {
    name: 'Directory Insights',
    description: 'Highlights data for every user identity, as events in a standard format you can query in real-time (via the admin portal or API).',
    icon: 'insightsDirectory',
  },
  [Attributes.deviceAndServerManagement]: {
    name: 'Device Management',
    description: 'Use the JumpCloud agent to securely manage your Windows, Mac, and Linux device fleet and servers, on-prem and in the cloud.',
    icon: 'devices',
  },
  [Attributes.deviceManagement]: {
    name: 'Device Management / MDM',
    description: 'Use JumpCloud\'s device management and MDM solutions to remotely and securely manage company-owned and personal devices. Set policies, execute commands, enforce MFA, and more.',
  },
  [Attributes.mdm]: {
    name: 'Mobile Device Management (MDM)',
    description: 'Mobile Device Management lets you securely and remotely configure your organization’s Apple and Windows devices, including updating software and device settings and monitoring compliance with your organization’s policies.',
    icon: 'mdm',
  },
  [Attributes.osPatchManagement]: {
    name: 'Patch Management',
    description: 'Enables you to easily manage OS and Browser patches for macOS, Windows, and Ubuntu (Linux) in a single console for greater visibility and reporting.',
    icon: 'patchManagement',
  },
  [Attributes.premiumSupport]: {
    name: 'Premium Support',
    description: '24x7 support & implementation support with guaranteed uptime SLA',
  },
  [Attributes.remoteAssist]: {
    name: 'Remote Assist',
    description: 'Use our free remote assist feature for remote desktop access and troubleshooting support from IT Admins for organizations of any size, for any number of devices, without limits on time or number of sessions.',
  },
  [Attributes.systemInsights]: {
    name: 'System Insights',
    description: 'Highlights device, server or other system resources as events in a standard format that you can query in real-time (via the admin portal or API).',
    icon: 'insightsSystem',
  },
  [Attributes.conditionalAccessPolicies]: {
    name: 'Conditional Access Policies',
    description: 'Ensures only appropriate users from known networks, known devices and with appropriate privileges, be granted access to company resources.',
    icon: 'conditionalPolicies',
  },
  [Attributes.deviceTrust]: {
    name: 'Device Trust',
    description: 'Set conditions, e.g. require MFA, on how users or groups can access their User Portal and applications from managed and unmanaged devices',
  },
  [Attributes.networkTrust]: {
    name: 'Network Trust',
    description: 'Set conditions, e.g. require MFA, on how users or groups can access their User Portal and applications from recognized or unrecognized IP addresses on your Conditional Lists',
  },
  [Attributes.supportAndImplementation]: {
    name: '24x7 support with guaranteed uptime SLA',
    shortName: '24x7 support with guaranteed uptime SLA',
    description: '24x7 support & implementation support with guaranteed uptime SLA',
    icon: 'support',
  },
  [Attributes.jumpcloudProtect]: {
    name: 'JumpCloud Protect',
    description: 'JumpCloud’s Push and TOTP multi-factor authentication mobile application that secures user’s access to applications and devices.',
  },
  [Attributes.passwordManagement]: {
    name: 'Password Manager',
    description: 'Use our full feature password manager to securely manage and share passwords, 2FA tokens, and other types of sensitive information, and gain full visibility and control over passwords used across your organization.',
    icon: 'passwordManager',
  },
};

const FeatureGroups = {
  cloudDirectory: {
    name: 'Cloud Directory',
    features: [
      {
        name: Features.cloudDirectory,
        attribute: Attributes.cloudDirectoryAndSupport,
      }, {
        name: Features.cloudDirectory,
        attribute: Attributes.jumpcloudProtect,
      },
    ],
  },
  coreCloudDirectory: {
    name: 'Cloud Directory including',
    features: [
      {
        name: Features.cloudDirectory,
        attribute: Attributes.cloudDirectoryAndSupport,
      }, {
        name: Features.cloudDirectory,
        attribute: Attributes.jumpcloudProtect,
      },
    ],
  },
  coreCloudDirectoryAndMFA: {
    name: 'Core Cloud Directory & MFA',
    features: [
      {
        name: Features.jumpcloudProtect,
        attribute: Attributes.jumpcloudProtect,
      },
      {
        name: Features.cloudDirectory,
        attribute: Attributes.cloudDirectoryAndSupport,
        infoPerPackage: {
          [SubscriptionTypes.alaCarte]: {
            listPrice: 3,
            annualPrice: 2,
          },
        },
      }, {
        name: Features.mfa,
        attribute: Attributes.cloudMFA,
        infoPerPackage: {
          [SubscriptionTypes.alaCarte]: {
            listPrice: 4,
            annualPrice: 3,
          },
        },
      }, {
        name: Features.passwordManagement,
        attribute: Attributes.passwordManagement,
        featureFlag: FeatureFlags.passwordManagerGtm,
        infoPerPackage: {
          [SubscriptionTypes.alaCarte]: {
            listPrice: 4,
            annualPrice: 3,
          },
          [SubscriptionTypes.sso]: {
            addOn: true,
            optional: true,
            listPrice: 4,
            annualPrice: 3,
          },
          [SubscriptionTypes.coreDirectory]: {
            addOn: true,
            optional: true,
            listPrice: 4,
            annualPrice: 3,
          },
          [SubscriptionTypes.platform]: {
            addOn: true,
            optional: true,
            listPrice: 4,
            annualPrice: 3,
          },
          [SubscriptionTypes.platformPlus]: {
            addOn: true,
            optional: true,
            listPrice: 4,
            annualPrice: 3,
          },
        },
      },
    ],
  },
  webSSO: {
    name: 'Web SSO',
    features: [
      {
        name: Features.sso,
        attribute: Attributes.SSOToWebApplications,
        infoPerPackage: {
          [SubscriptionTypes.alaCarte]: {
            listPrice: 4,
            annualPrice: 3,
          },
        },
      },
      {
        name: Features.userLifecycle,
        attribute: Attributes.userLifecycleManagement,
        infoPerPackage: {
          [SubscriptionTypes.alaCarte]: {
            listPrice: 4,
            annualPrice: 3,
          },
        },
      },
    ],
  },
  webSSOCustom: {
    name: 'Plus',
    features: [
      {
        name: Features.mfa,
        attribute: Attributes.cloudMFA,
      },
      {
        name: Features.sso,
        attribute: Attributes.SSOToWebApplications,
      },
      {
        name: Features.userLifecycle,
        attribute: Attributes.userLifecycleManagement,
      },
    ],
  },
  accessManagementAndLogging: {
    name: 'Access Management & Logging',
    features: [
      {
        name: Features.ldap,
        attribute: Attributes.cloudLDAP,
        infoPerPackage: {
          [SubscriptionTypes.alaCarte]: {
            listPrice: 4,
            annualPrice: 3,
          },
        },
      },
      {
        name: Features.radius,
        attribute: Attributes.cloudRADIUS,
        infoPerPackage: {
          [SubscriptionTypes.alaCarte]: {
            listPrice: 4,
            annualPrice: 3,
          },
        },
      },
      {
        name: Features.directoryInsightsPremium,
        attribute: Attributes.directoryInsights,
        infoPerPackage: {
          [SubscriptionTypes.alaCarte]: {
            listPrice: 4,
            annualPrice: 3,
          },
        },
      },
    ],
  },
  deviceManagementAndTelemetry: {
    name: 'Device Management & Telemetry',
    features: [
      {
        name: Features.remoteAssist,
        attribute: Attributes.remoteAssist,
      },
      {
        name: Features.deviceManagement,
        attribute: Attributes.deviceAndServerManagement,
        infoPerPackage: {
          [SubscriptionTypes.alaCarte]: {
            listPrice: 6,
            annualPrice: 5,
          },
          [SubscriptionTypes.sso]: {
            addOn: true,
            optional: true,
            listPrice: 5,
            annualPrice: 4,
          },
          [SubscriptionTypes.coreDirectory]: {
            addOn: true,
            optional: true,
            listPrice: 5,
            annualPrice: 4,
          },
        },
      },
      {
        name: Features.mdm,
        attribute: Attributes.mdm,
        infoPerPackage: {
          [SubscriptionTypes.alaCarte]: {
            included: 'Included w/ Device Management',
          },
          [SubscriptionTypes.sso]: {
            included: 'Included w/ Device Management',
          },
          [SubscriptionTypes.coreDirectory]: {
            included: 'Included w/ Device Management',
          },
        },
      },
      {
        name: Features.systemInsights,
        attribute: Attributes.systemInsights,
        infoPerPackage: {
          [SubscriptionTypes.alaCarte]: {
            listPrice: 4,
            annualPrice: 3,
          },
          [SubscriptionTypes.sso]: {
            addOn: true,
            optional: true,
            listPrice: 4,
            annualPrice: 3,
          },
          [SubscriptionTypes.coreDirectory]: {
            addOn: true,
            optional: true,
            listPrice: 4,
            annualPrice: 3,
          },
        },
      },
      {
        name: Features.osPatchManagement,
        optionalAddon: true,
        attribute: Attributes.osPatchManagement,
        infoPerPackage: {
          [SubscriptionTypes.alaCarte]: {
            listPrice: 4,
            annualPrice: 3,
          },
          [SubscriptionTypes.sso]: {
            addOn: true,
            optional: true,
            listPrice: 4,
            annualPrice: 3,
          },
          [SubscriptionTypes.coreDirectory]: {
            addOn: true,
            optional: true,
            listPrice: 4,
            annualPrice: 3,
          },
          [SubscriptionTypes.platform]: {
            addOn: true,
            optional: true,
            listPrice: 4,
            annualPrice: 3,
          },
          [SubscriptionTypes.platformPlus]: {
            addOn: true,
            optional: true,
            listPrice: 4,
            annualPrice: 3,
          },
        },
      },
    ],
  },
  zeroTrust: {
    name: 'Zero Trust',
    features: [
      {
        name: Features.zeroTrust,
        attribute: Attributes.conditionalAccessPolicies,
        infoPerPackage: {
          [SubscriptionTypes.alaCarte]: {
            listPrice: 4,
            annualPrice: 3,
          },
        },
      },
      {
        name: Features.zeroTrust,
        attribute: Attributes.deviceTrust,
        infoPerPackage: {
          [SubscriptionTypes.alaCarte]: {
            included: 'Included w/ Conditional Access',
          },
        },
      },
      {
        name: Features.zeroTrust,
        attribute: Attributes.networkTrust,
        infoPerPackage: {
          [SubscriptionTypes.alaCarte]: {
            included: 'Included w/ Conditional Access',
          },
        },
      },
    ],
  },
  premiumSupport: {
    name: 'Premium Support',
    features: [
      {
        name: Features.premiumSupport,
        attribute: Attributes.supportAndImplementation,
        infoPerPackage: {
          [SubscriptionTypes.alaCarte]: {
            listPrice: 3,
            annualPrice: 2,
          },
          [SubscriptionTypes.sso]: {
            addOn: true,
            optional: true,
            listPrice: 3,
            annualPrice: 2,
          },
          [SubscriptionTypes.coreDirectory]: {
            addOn: true,
            optional: true,
            listPrice: 3,
            annualPrice: 2,
          },
          [SubscriptionTypes.platform]: {
            addOn: true,
            optional: true,
            listPrice: 3,
            annualPrice: 2,
          },
        },
      },
    ],
  },
};

const SubscriptionDetails = {
  [SubscriptionTypes.alaCarte]: {
    contactSales: true,
    description: 'Cloud Directory + Features you need.',
    specification: {
      description: 'Build what you need:',
      groups: [
        FeatureGroups.cloudDirectory,
      ],
    },
  },
  [SubscriptionTypes.coreDirectory]: {
    description: 'Identity & access management',
    specification: {
      description: 'Everything in SSO, plus:',
      groups: [
        FeatureGroups.accessManagementAndLogging,
      ],
    },
  },
  [SubscriptionTypes.platform]: {
    bestValue: true,
    description: 'Complete IAM & device management.',
    specification: {
      description: 'Everything in Core Directory, plus:',
      groups: [
        FeatureGroups.deviceManagementAndTelemetry,
      ],
    },
  },
  [SubscriptionTypes.platformPlus]: {
    description: 'Advanced security through Zero Trust',
    specification: {
      description: 'Everything in Platform, plus:',
      groups: [
        FeatureGroups.zeroTrust,
        FeatureGroups.premiumSupport,
      ],
    },
  },
  [SubscriptionTypes.sso]: {
    description: 'User Provisioning & SSO to web apps',
    specification: {
      description: 'What\'s included:',
      groups: [
        FeatureGroups.coreCloudDirectory,
        FeatureGroups.webSSOCustom,
      ],
    },
  },
};

const ProductCodeToSubscriptionType = {
  'BYOD-2021': SubscriptionTypes.alaCarte,
  'CORE-DIRECTORY-2021': SubscriptionTypes.coreDirectory,
  'PLATFORM-2021': SubscriptionTypes.platform,
  'PLATFORM-PLUS-2021': SubscriptionTypes.platformPlus,
  'SSO-PACKAGE-2021': SubscriptionTypes.sso,
  'PRIME-PASS': SubscriptionTypes.primePlus,
  DEVICES: SubscriptionTypes.devices,
  'PATCH-DEVICES': SubscriptionTypes.patchDevices,
  'POLICY-DEVICES': SubscriptionTypes.policyDevices,
};

const getProductCodeByType = (type) => invert(ProductCodeToSubscriptionType)[type];

// features and corresponding market prices used to compare
// against Jumpcloud packages on pricing calculator
const ComparisonFeatures = {
  conditionalAccess: { name: 'Conditional Access', price: 8 },
  logging: { name: 'Logging', price: 5 },
  ldap: { name: 'LDAP', price: 3 },
  mdm: { name: 'MDM', price: 8 },
  mfa: { name: 'MFA', price: 5 },
  radius: { name: 'RADIUS', price: 3 },
  sso: { name: 'SSO', price: 6 },
  userLifecycle: { name: 'User Lifecycle', price: 6 },
  vpn: { name: 'VPN', price: 3 },
};

const CompetitiveComparisonFeaturesByPackage = {
  [SubscriptionTypes.sso]: [
    ComparisonFeatures.mfa,
    ComparisonFeatures.sso,
    ComparisonFeatures.userLifecycle,
  ],
  [SubscriptionTypes.coreDirectory]: [
    ComparisonFeatures.ldap,
    ComparisonFeatures.mfa,
    ComparisonFeatures.radius,
    ComparisonFeatures.sso,
    ComparisonFeatures.userLifecycle,
    ComparisonFeatures.vpn,
  ],
  [SubscriptionTypes.platform]: [
    ComparisonFeatures.ldap,
    ComparisonFeatures.logging,
    ComparisonFeatures.mdm,
    ComparisonFeatures.mfa,
    ComparisonFeatures.radius,
    ComparisonFeatures.sso,
    ComparisonFeatures.userLifecycle,
    ComparisonFeatures.vpn,
  ],
  [SubscriptionTypes.platformPlus]: [
    ComparisonFeatures.conditionalAccess,
    ComparisonFeatures.ldap,
    ComparisonFeatures.logging,
    ComparisonFeatures.mdm,
    ComparisonFeatures.mfa,
    ComparisonFeatures.radius,
    ComparisonFeatures.sso,
    ComparisonFeatures.userLifecycle,
    ComparisonFeatures.vpn,
  ],
};

// HACK: there are some data issues coming from BSS since there are discrepancies
// for add-ons/components, both legacy and current ones.
// In the account page we display a feature list and an add-on list.
// Because of the discrepancies, we might end up displaying related some features/add-ons
// in the wrong place.
// This next object is for tracking and solve this issue
const FeaturesAddOnsDuplicatedHackList = {
  [Features.cloudDirectory]: [
    'Cloud Directory',
    'Cloud Directory (Monthly)',
    'Cloud Directory (Annual)',
  ],
  [Features.sso]: [
    'SSO',
    'SSO (Monthly)',
  ],
  [Features.userLifecycle]: [
    'User Lifecycle Management',
  ],
  [Features.ldap]: [
    'Cloud LDAP',
    'LDAP (Monthly)',
    'LDAP (Annual)',
  ],
  [Features.radius]: [
    'Cloud Radius',
    'Radius (Monthly)',
  ],
  [Features.directoryInsightsPremium]: [
    'Directory Insights',
    'Directory Insights (Monthly)',
    'Directory Insights (Annual)',
  ],
  [Features.deviceManagement]: [
    'Device Management / MDM',
    'Systems (Annual)',
    'Systems (Monthly)',
  ],
  [Features.mdm]: [
    'Device Management / MDM',
    'Systems (Annual)',
    'Systems (Monthly)',
  ],
  [Features.systemInsights]: [
    'Systems',
    'System Insights',
    'System Insights (Annual)',
  ],
  [Features.zeroTrust]: [
    'Conditional Access',
  ],
  [Features.premiumSupport]: [
    'Premium Support',
    'Premium Support (Annual)',
  ],
  [Features.mfa]: [
    'MFA',
  ],
};

const AddOnStates = {
  add: 'add',
  remove: 'remove',
};

export default {
  getProductCodeByType,
  AddOnProductCodes,
  AddOnStates,
  Attributes,
  AttributeDetails,
  CompetitiveComparisonFeaturesByPackage,
  Features,
  FeaturesAddOnsDuplicatedHackList,
  FeatureGroups,
  ProductCodeToSubscriptionType,
  SubscriptionDetails,
  SubscriptionFields,
  SubscriptionTypes,
};
